import { DndContext, type DragEndEvent, type SensorDescriptor, type SensorOptions, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { Post } from '../types';
import { PLATFORM_ICONS, PLATFORM_DISPLAY_NAMES, getStatusColor, getPostTypeBadgeColor } from '../constants';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from "@/helpers/date.ts";

interface TableViewProps {
  posts: Post[];
  sensors: SensorDescriptor<SensorOptions>[];
  handleDragEnd: (event: DragEndEvent) => void;
  handlePostClick: (post: Post) => void;
}

function getStatusLabel(status: string): string {
  switch(status) {
    case 'draft': return 'Draft';
    case 'approved': return 'Approved';
    case 'scheduled': return 'Scheduled';
    case 'posted': return 'Posted';
    default: return 'Unknown';
  }
}

function getPostTypeLabel(postType?: string): string {
  switch(postType) {
    case 'photo': return 'Photo';
    case 'video': return 'Video';
    case 'link': return 'Link';
    case 'reel': return 'Reel';
    default: return 'Text';
  }
}

interface TableRowItemProps {
  post: Post;
  onClick: () => void;
}

function TableRowItem({ post, onClick }: TableRowItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: post.id!,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative' as const,
  };

  const iconSrc = PLATFORM_ICONS[post.platform];
  const firstImage = post.attachments?.find(a => a.type === 'image')?.url;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className="hover:bg-accent cursor-pointer"
      onClick={onClick}
    >
      <TableCell className="w-8">
        <div
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </TableCell>
      <TableCell className="w-24">
        <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium ${getPostTypeBadgeColor(post.postType)}`}>
          {getPostTypeLabel(post.postType)}
        </span>
      </TableCell>
      <TableCell className="w-28 font-medium">{formatDate(post.date)}</TableCell>
      <TableCell className="w-20">{post.time}</TableCell>
      <TableCell className="w-28">
        <div className="flex items-center gap-2">
          {iconSrc && (
            <img src={iconSrc} alt="" className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="text-sm">{PLATFORM_DISPLAY_NAMES[post.platform as keyof typeof PLATFORM_DISPLAY_NAMES] || post.platform.replace('_', ' ')}</span>
        </div>
      </TableCell>
      <TableCell className="w-32 font-medium truncate">{post.topic}</TableCell>
      <TableCell className="max-w-md">
        <div className="truncate text-sm text-muted-foreground">{post.text}</div>
      </TableCell>
      <TableCell className="w-24">
        {post.status && (
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(post.status)}`} />
            <span className="text-xs">{getStatusLabel(post.status)}</span>
          </div>
        )}
      </TableCell>
      <TableCell className="w-16">
        {firstImage && (
          <div className="w-12 h-12 bg-muted/20 rounded overflow-hidden">
            <img
              src={firstImage}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export function TableView({ posts, sensors, handleDragEnd, handlePostClick }: TableViewProps) {
  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-muted-foreground text-sm">No posts scheduled</p>
      </div>
    );
  }

  const postIds = posts.map(post => post.id!);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
    >
      <div className="overflow-x-auto h-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-24">Type</TableHead>
              <TableHead className="w-28">Date</TableHead>
              <TableHead className="w-20">Time</TableHead>
              <TableHead className="w-28">Platform</TableHead>
              <TableHead className="w-32">Topic</TableHead>
              <TableHead className="max-w-md">Content</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-16">Image</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <SortableContext items={postIds} strategy={verticalListSortingStrategy}>
              {posts.map((post) => (
                <TableRowItem
                  key={post.id}
                  post={post}
                  onClick={() => handlePostClick(post)}
                />
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </div>
    </DndContext>
  );
}
